import { useOutletContext } from "react-router";

/**
 * Sözleşme detay kaydının BAŞLIK yükü (`GET /api/contracts/:id`).
 *
 * ⚠ Bu, sözleşmenin TAMAMI değil: taraflar, tarihler, süreçler, yorumlar kendi uçlarından
 * okunuyor ("her bölüm kendi kanonik kaynağından" — `ContractDetailLayout`). Burada
 * yalnız kabuğun ve birden çok bölümün ortak ihtiyaç duyduğu künye var.
 */
export interface ContractDetailRecord {
  id: string;
  title: string;
  status: string;
  contractType: string | null;
  description: string | null;
  referenceNumber: string | null;
  currency: string | null;
  value: string | null;
  tags: string[];
  version: number;
  /**
   * Sunucu hangi alanların düzenlenebilir olduğunu SÖYLÜYOR (türe bağlı katalog).
   *
   * ⚠ Bu bir YETKİ beyanı DEĞİL: yalnız sözleşme TÜRÜNE göre süzülüyor ve aynı türün her
   * görüntüleyicisine aynı liste gidiyor (CLOS-888).
   */
  editableFields: string[];
  types: { code: string; label: string }[];
  /**
   * GÖRÜNTÜLEYENİN bu kayıttaki efektif düzeyi (CLOS-888).
   *
   * ⚠ OPSİYONEL ve yokluğu "yazamaz" saymıyor — gerekçe `kayitYazma` türetiminde.
   */
  viewerAccessLevel?: "read" | "write";
}

/**
 * Kabuğun bölümlere geçirdiği ORTAK karar kümesi.
 *
 * ### ⚠ Neden prop değil de outlet bağlamı
 *
 * Bölümler artık ayrı rotalar; React Router çocuğu kendisi çiziyor, kabuk ona prop
 * geçiremiyor. İki seçenek vardı:
 *
 * 1. Her bölüm `GET /api/contracts/:id`'i KENDİ sorgusuyla okusun (anahtar aynı olduğu
 *    için ağ maliyeti yok, önbellek paylaşılıyor).
 * 2. Kabuk bir kez okusun, bağlamla dağıtsın.
 *
 * İkincisi seçildi çünkü kabuk yalnız veriyi değil ERİŞİM KARARINI da veriyor: kayıt yok
 * ya da yetki yoksa tek bir `StateView` çiziliyor ve hiçbir bölüm çizilmiyor. Birinci
 * seçenekte her bölüm kendi yükleme/hata durumunu ayrıca yönetirdi ve aynı 404 beş yerde
 * beş kez ele alınırdı — kabuğun kendi kuralı ("bölümler bağımsız yüklenir ama AYNI
 * erişim kararı altında") bozulurdu.
 *
 * ⚠ Bağlamda yalnız TÜRETİLMİŞ karar ve künye var; bölüm verisi YOK. Bir bölümün
 * verisini buraya koymak, kabuğu o bölümün yükleme durumuna bağlar ve bölümlerin bağımsız
 * düşme özelliğini kaybettirirdi.
 */
export interface ContractDetailContext {
  /** Rota parametresi — bölümler kendi sorgularını bununla anahtarlıyor. */
  id: string;
  /** Kaydın künyesi. */
  s: ContractDetailRecord;
  /**
   * KAYIT DÜZEYİ YAZMA (CLOS-888).
   *
   * ⚠ Rol yetkisi TEK BAŞINA yetmiyor: `standard_user` rollerinin tamamı `contract.edit`
   * taşıyor, yani kayda yalnız `read` verilen üye bütün yazma yüzeylerini görüyordu.
   * Sunucu her zaman fail-closed'du; sızan şey yetki değil DÜRÜSTLÜKTÜ.
   */
  kayitYazma: boolean;
  /** Arşivdeki kayıtta yeni akış başlatılamıyor; sunucunun kuralı ekrana taşınıyor. */
  arsivde: boolean;
  /**
   * Geliş bağlamı (`?liste=...`) — kullanıcı daraltılmış listeye geri dönebilsin.
   * Adreste tutuluyor, state'te değil: state yenilemede ve paylaşılan bağlantıda kaybolur.
   */
  listeSorgusu: string;
  /** Listeye dönüş adresi, geliş bağlamı korunmuş hâlde. */
  listeYolu: string;
}

export function useContractDetail(): ContractDetailContext {
  return useOutletContext<ContractDetailContext>();
}
